import React, { useState, useRef } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { Heart, X, Star, MapPin, Shield, Languages } from 'lucide-react';

export const SwipeCard = ({ profile, onSwipe, isTop }) => {
  const [exitX, setExitX] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-30, 30]);
  const likeOpacity = useTransform(x, [0, 100], [0, 1]);
  const nopeOpacity = useTransform(x, [-100, 0], [1, 0]);

  const handleDragEnd = (event, info) => {
    if (info.offset.x > 100) {
      setExitX(300);
      setIsExiting(true);
      setTimeout(() => onSwipe('like'), 300);
    } else if (info.offset.x < -100) {
      setExitX(-300);
      setIsExiting(true);
      setTimeout(() => onSwipe('pass'), 300);
    } else {
      animate(x, 0, { type: 'spring', stiffness: 500, damping: 30 });
    }
  };

  const handleButtonSwipe = (action) => {
    if (action === 'like' || action === 'super_like') {
      setExitX(300);
    } else {
      setExitX(-300);
    }
    setIsExiting(true);
    setTimeout(() => onSwipe(action), 300);
  };

  if (!profile) return null;

  const mainPhoto = profile.photos?.[0] || 'https://images.unsplash.com/photo-1745572587190-962fe038f073?crop=entropy&cs=srgb&fm=jpg&q=85';

  return (
    <motion.div
      className="absolute w-full"
      style={{ x, rotate, zIndex: isTop ? 10 : 0 }}
      drag={isTop ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={handleDragEnd}
      animate={isExiting ? { x: exitX, opacity: 0 } : {}}
      transition={{ duration: 0.3 }}
    >
      <div 
        className="relative overflow-hidden rounded-3xl aspect-[3/4] shadow-2xl border border-white/10 bg-dark-paper swipe-card"
        data-testid="swipe-card"
      >
        {/* Photo */}
        <img
          src={mainPhoto}
          alt={profile.user_name || 'Profile'}
          className="w-full h-full object-cover"
          draggable={false}
        />

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

        {/* Like indicator */}
        <motion.div
          className="absolute top-8 right-8 px-6 py-2 border-4 border-green-500 rounded-lg rotate-12"
          style={{ opacity: likeOpacity }}
        >
          <span className="text-green-500 font-bold text-3xl tracking-wider">LIKE</span>
        </motion.div>

        {/* Nope indicator */}
        <motion.div
          className="absolute top-8 left-8 px-6 py-2 border-4 border-red-500 rounded-lg -rotate-12"
          style={{ opacity: nopeOpacity }}
        >
          <span className="text-red-500 font-bold text-3xl tracking-wider">NOPE</span>
        </motion.div>

        {/* Profile info */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="font-heading text-3xl font-bold text-white">
              {profile.user_name || 'User'}
            </h2>
            <span className="text-2xl text-white/80">{profile.age}</span>
            {profile.is_verified && (
              <Shield className="w-6 h-6 text-gold" fill="#D4AF37" />
            )}
          </div>

          <div className="flex items-center gap-2 text-white/70 mb-3">
            <MapPin className="w-4 h-4" />
            <span className="font-body text-sm">
              {profile.location_city ? `${profile.location_city}, ` : ''}
              {profile.location_country}
            </span>
          </div>

          {profile.languages && profile.languages.length > 0 && (
            <div className="flex items-center gap-2 text-white/70 mb-3">
              <Languages className="w-4 h-4" />
              <span className="font-body text-sm">{profile.languages.slice(0, 3).join(', ')}</span>
            </div>
          )}

          {profile.bio && (
            <p className="font-body text-sm text-white/80 line-clamp-2">{profile.bio}</p>
          )}

          {profile.intent && (
            <div className="mt-3">
              <span className="inline-block px-3 py-1 bg-gold/20 text-gold text-xs font-medium rounded-full border border-gold/30">
                {profile.intent.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      {isTop && (
        <div className="flex justify-center gap-6 mt-6">
          <button
            onClick={() => handleButtonSwipe('pass')}
            className="w-16 h-16 rounded-full bg-dark-elevated border border-white/10 flex items-center justify-center hover:border-red-500/50 hover:bg-red-500/10 transition-colors"
            data-testid="pass-button"
          >
            <X className="w-8 h-8 text-red-500" />
          </button>

          <button
            onClick={() => handleButtonSwipe('super_like')}
            className="w-14 h-14 rounded-full bg-dark-elevated border border-gold/30 flex items-center justify-center hover:bg-gold/10 transition-colors animate-pulse-gold"
            data-testid="super-like-button"
          >
            <Star className="w-6 h-6 text-gold" fill="#D4AF37" />
          </button>

          <button
            onClick={() => handleButtonSwipe('like')}
            className="w-16 h-16 rounded-full bg-dark-elevated border border-white/10 flex items-center justify-center hover:border-green-500/50 hover:bg-green-500/10 transition-colors"
            data-testid="like-button"
          >
            <Heart className="w-8 h-8 text-green-500" />
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default SwipeCard;
